import { Award, Calculator, ChevronRight } from 'lucide-react';
import { useState, type ChangeEvent } from 'react';
import { formations } from '../lib/formations';
import { players } from '../data/seed';
import type { Prediction } from '../types';

interface Props {
  predictions: Prediction[];
  onScore: (id: string, actual: Partial<Prediction>) => Promise<void>;
}

const playerName = (id?: string) => players.find(player => player.id === id)?.shortName || '—';

export function Ledger({ predictions, onScore }: Props) {
  const [scoring, setScoring] = useState<Prediction | null>(null);
  const [actualFormation, setActualFormation] = useState('4-2-3-1');
  const [actualXI, setActualXI] = useState<string[]>([]);
  const [actualFirstScorer, setActualFirstScorer] = useState('benjamin-sesko');
  const [actualMotm, setActualMotm] = useState('bruno-fernandes');
  const [actualHome, setActualHome] = useState(2);
  const [actualAway, setActualAway] = useState(1);

  const openScoring = (prediction: Prediction) => {
    setScoring(prediction);
    setActualFormation(prediction.formation);
    setActualXI([...prediction.startingXI]);
    setActualFirstScorer(prediction.firstScorer);
    setActualMotm(prediction.motm);
    setActualHome(prediction.homeScore);
    setActualAway(prediction.awayScore);
  };

  const submit = async () => {
    if (!scoring) return;
    await onScore(scoring.id, {
      actualFormation, actualStartingXI: actualXI, actualFirstScorer, actualMotm,
      actualHomeScore: actualHome, actualAwayScore: actualAway,
    });
    setScoring(null);
  };

  return (
    <section className="section-shell ledger-shell" id="ledger">
      <div className="section-heading">
        <div><p className="section-kicker"><Calculator size={15}/> Prediction scoring database</p><h2>The ledger</h2><p>Every saved match call stays auditable. Score completed matches against the actual XI, formation, first scorer, MOTM and final score.</p></div>
        <div className="ledger-total"><small>Predictions</small><strong>{predictions.length}</strong></div>
      </div>

      {predictions.length === 0 ? (
        <div className="empty-ledger"><Award size={34}/><strong>No predictions saved yet</strong><span>Build the next XI above and save it to start the database.</span></div>
      ) : (
        <div className="ledger-list">
          {predictions.map(item => (
            <article className="ledger-row" key={item.id}>
              <div className="ledger-date"><strong>{new Date(item.fixtureDate + 'T12:00:00').toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })}</strong><span>{item.status}</span></div>
              <div className="ledger-match"><strong>United v {item.opponent}</strong><span>{item.formation} · {item.homeScore}–{item.awayScore} · scorer {playerName(item.firstScorer)}</span></div>
              <div className="ledger-xi"><span>XI</span><strong>{item.startingXI.map(playerName).join(' · ')}</strong></div>
              <div className="ledger-score"><small>Points</small><strong>{item.scoreTotal ?? '—'}<span>/120</span></strong></div>
              <button onClick={() => openScoring(item)}>{item.status === 'scored' ? 'Rescore' : 'Enter result'}<ChevronRight size={16}/></button>
              {item.scoreBreakdown && <div className="breakdown"><span>XI {item.scoreBreakdown.lineup}</span><span>Shape {item.scoreBreakdown.formation}</span><span>Scorer {item.scoreBreakdown.firstScorer}</span><span>MOTM {item.scoreBreakdown.motm}</span><span>Score {item.scoreBreakdown.finalScore}</span></div>}
            </article>
          ))}
        </div>
      )}

      {scoring && (
        <div className="modal-backdrop" role="dialog" aria-modal="true" aria-label="Score prediction">
          <div className="score-modal">
            <div className="modal-head"><div><small>Actual match data</small><h3>Score United v {scoring.opponent}</h3></div><button onClick={() => setScoring(null)}>Close</button></div>
            <label>Actual formation<select value={actualFormation} onChange={(event: ChangeEvent<HTMLSelectElement>) => setActualFormation(event.target.value)}>{Object.keys(formations).map(item => <option key={item}>{item}</option>)}</select></label>
            <label>Actual starting XI<select multiple value={actualXI} onChange={(event: ChangeEvent<HTMLSelectElement>) => setActualXI(Array.from(event.currentTarget.selectedOptions).map(option => option.value))}>{players.filter(player => player.selectable !== false).map(player => <option key={player.id} value={player.id}>{player.name}</option>)}</select><small>Select exactly 11 players. On mobile, tap each name.</small></label>
            <div className="modal-two"><label>First scorer<select value={actualFirstScorer} onChange={(event: ChangeEvent<HTMLSelectElement>) => setActualFirstScorer(event.target.value)}>{players.filter(player => player.selectable !== false && player.group !== 'GK').map(player => <option key={player.id} value={player.id}>{player.name}</option>)}</select></label><label>MOTM<select value={actualMotm} onChange={(event: ChangeEvent<HTMLSelectElement>) => setActualMotm(event.target.value)}>{players.filter(player => player.selectable !== false).map(player => <option key={player.id} value={player.id}>{player.name}</option>)}</select></label></div>
            <div className="modal-two"><label>United score<input type="number" min="0" value={actualHome} onChange={(event: ChangeEvent<HTMLInputElement>) => setActualHome(Number(event.target.value))}/></label><label>Opponent score<input type="number" min="0" value={actualAway} onChange={(event: ChangeEvent<HTMLInputElement>) => setActualAway(Number(event.target.value))}/></label></div>
            <button className="primary-button" disabled={actualXI.length !== 11} onClick={submit}><Award size={18}/>Calculate points</button>
          </div>
        </div>
      )}
    </section>
  );
}
